import { RepairListResponseSchema, type RepairListResponse } from '@/lib/service/ecs/contracts';
import { devError } from '@/lib/utils/devLog';

const REPAIR_LIST_GENERIC_FAILURE = 'REPAIR_LIST_GENERIC_FAILURE';

export interface AuthenticatedRepairListQuery {
  countryCode: string;
  page?: number;
  repairNumber?: string;
  watchSerial?: string;
}

export interface GuestRepairListQuery {
  countryCode: string;
  email: string;
  /** Legacy URL `code` value; sent to BFF as `accessCode`. */
  accessCode: string;
  page?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readErrorMessage(json: unknown): string {
  if (!isRecord(json) || !Array.isArray(json.errors)) return '';
  const first = json.errors[0];
  if (isRecord(first) && typeof first.message === 'string' && first.message) return first.message;
  return '';
}

function appendOptionalParams(
  params: URLSearchParams,
  query: { page?: number; repairNumber?: string; watchSerial?: string }
): void {
  if (query.page !== undefined && query.page > 0) {
    params.set('page', String(query.page));
  }
  if (query.repairNumber) params.set('repairNumber', query.repairNumber);
  if (query.watchSerial) params.set('watchSerial', query.watchSerial);
}

export function buildAuthenticatedRepairListUrl(query: AuthenticatedRepairListQuery): string {
  const params = new URLSearchParams({ countryCode: query.countryCode });
  appendOptionalParams(params, query);
  return `/api/service/repair?${params.toString()}`;
}

export function buildGuestRepairListUrl(query: GuestRepairListQuery): string {
  const params = new URLSearchParams({
    countryCode: query.countryCode,
    email: query.email,
    accessCode: query.accessCode,
  });
  appendOptionalParams(params, query);
  return `/api/service/repair/guest?${params.toString()}`;
}

async function fetchRepairListFromUrl(
  url: string,
  credentials: RequestCredentials = 'same-origin'
): Promise<RepairListResponse> {
  let response: Response;
  try {
    response = await fetch(url, { credentials });
  } catch (error) {
    devError('[repairDetailsClient] request failed:', error);
    throw new Error(REPAIR_LIST_GENERIC_FAILURE, { cause: error });
  }

  const json: unknown = await response.json().catch(() => null);
  if (!response.ok || !isRecord(json) || json.success !== true) {
    throw new Error(readErrorMessage(json) || REPAIR_LIST_GENERIC_FAILURE);
  }

  const parsed = RepairListResponseSchema.safeParse(json.data ?? {});
  if (!parsed.success) throw new Error(REPAIR_LIST_GENERIC_FAILURE);
  return parsed.data;
}

export async function fetchAuthenticatedRepairList(
  query: AuthenticatedRepairListQuery
): Promise<RepairListResponse> {
  return fetchRepairListFromUrl(buildAuthenticatedRepairListUrl(query));
}

export async function fetchGuestRepairList(
  query: GuestRepairListQuery
): Promise<RepairListResponse> {
  return fetchRepairListFromUrl(buildGuestRepairListUrl(query), 'omit');
}
